import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { FolderList } from '@workspace/view-model-folder/ui/FolderList';
import { ViewModelList } from '@workspace/view-model-folder/ui/ViewModelList';
import type { FolderId, ViewModelId } from 'balus-common';
import type { Workspace } from '@workspace/domain/workspace/Workspace';
import { Droppable } from '@workspace/view-model-folder/ui/DragAndDrop';
import { ViewModelEntity, ViewModelOperation } from '@view-model/domain/view-model';
import { useActionLogSender } from '@framework/action-log';
import { EmptyMessage } from './EmptyMessage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { MenuButtons } from './MenuButtons';
import { useCreateViewModelFromClipboardPayloadHandler } from '@workspace/view-model-folder/hooks';
import { ViewModelCreateModal } from '../ViewModelCreateModal';
import { FolderCreateModal } from '../FolderCreateModal';
import { Link } from 'react-router';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { Folder } from '@workspace/view-model-folder/domain/Folder';
import { MenuButtons as FolderMenuButtons } from '../FolderItem';
import { FolderEditModal } from '../FolderEditModal';
import { FolderDeleteModal } from '../FolderDeleteModal';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';
import { ObjectRepository } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { Loading } from '@framework/ui/atoms';
import { MoveViewModelConfirmModal } from '../MoveModal/MoveViewModelConfirmModal';
import { MoveFolderModal } from '../MoveModal/MoveFolderModal';
import { MoveToOtherWorkspaceModal } from '../MoveToOtherWorkspaceModal';
import { useCurrentUserId } from '@framework/auth';
import { MoveFolderConfirmModal } from '../MoveModal/MoveFolderConfirmModal';
import { useViewModelIndividualPermissionEnabled } from '@workspace/domain/useViewModelIndividualPermissionEnabled';
import { setScrolledViewModelAtom } from '@user/Sidebar/SidebarContainer/SidebarTree/SidebarTreeStore/sidebarTreeControlState';
import { useSetAtom } from 'jotai';
import { openFoldersAtom } from '@user/Sidebar/SidebarContainer/SidebarTree/SidebarTreeStore/sidebarTreeOpenState';
import { useStableIds } from '@framework/hooks';

type ModalTarget =
  'CreateViewModel' | 'CreateFolder' | 'EditFolder' | 'DeleteFolder' | 'MoveFolder' | 'MoveFolderToOtherWorkspace';

type Props = {
  showEditMenu: boolean;
  workspace: Workspace;
  rootFolderTree: RootFolderTree;
  folderId: FolderId;
};

export const RootFolderContent: React.FC<Props> = ({ showEditMenu, workspace, rootFolderTree, folderId }: Props) => {
  const { t } = useTranslation();
  const actionLogSender = useActionLogSender();
  const currentUserId = useCurrentUserId();

  const [currentFolder, setCurrentFolder] = useState<Folder | null | 'loading'>('loading');

  useEffect(() => {
    if (folderId === Folder.ROOT_ID) {
      setCurrentFolder(Folder.buildRootFolder(workspace.id));
      return;
    }

    const repo = new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(folderId));
    repo.addListener((folder) => setCurrentFolder(folder));
    return () => repo.removeListener();
  }, [folderId, workspace.id]);

  const [parentFolder, setParentFolder] = useState<Folder | null>(null);

  useEffect(() => {
    const parentFolderId = rootFolderTree.getParentFolderId(folderId);
    if (!parentFolderId) {
      setParentFolder(null);
      return; // 現在表示中のフォルダが最上位階層の場合、親フォルダIDは null が返る
    }

    if (parentFolderId === Folder.ROOT_ID) {
      setParentFolder(Folder.buildRootFolder(workspace.id));
      return;
    }

    const repo = new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(parentFolderId));
    repo.addListener((folder) => setParentFolder(folder));
    return () => repo.removeListener();
  }, [folderId, rootFolderTree, workspace.id]);

  const [droppableToParentFolder, setDroppableToParentFolder] = useState<boolean>(false);

  const handleDragStart = (event: DragStartEvent) => {
    const viewModelId = event.active.id as string; // id が string | number なので、 string に限定する
    setDroppableToParentFolder(rootFolderTree.getParentFolderId(viewModelId) !== Folder.ROOT_ID);
  };

  // ビューモデルの個別権限設定の表示可否フラグ
  const showConfigureIndividualPermissions = useViewModelIndividualPermissionEnabled(workspace.id);

  const [modalTarget, setModalTarget] = useState<ModalTarget | null>(null);
  const handleClickCreateViewModel = useCallback(() => setModalTarget('CreateViewModel'), []);
  const handleClickEditFolder = useCallback(() => setModalTarget('EditFolder'), []);
  const handleClickDeleteFolder = useCallback(() => setModalTarget('DeleteFolder'), []);
  const handleClickMoveFolder = useCallback(() => setModalTarget('MoveFolder'), []);
  const handleClickMoveFolderToOtherWorkspace = useCallback(() => setModalTarget('MoveFolderToOtherWorkspace'), []);
  const handleClickCreateFolder = useCallback(() => setModalTarget('CreateFolder'), []);
  const handleCloseModal = useCallback(() => setModalTarget(null), []);

  const createViewModelFromClipboardPayload = useCreateViewModelFromClipboardPayloadHandler(
    workspace.groupId,
    workspace.id,
    folderId
  );

  const handlePaste = () => createViewModelFromClipboardPayload();

  const setScrolledViewModel = useSetAtom(setScrolledViewModelAtom);
  const openFolders = useSetAtom(openFoldersAtom);

  const handleCreateViewModelToRoot = useCallback(
    async (name: string) => {
      const viewModelId = await ViewModelOperation.create(workspace.id, folderId, name, actionLogSender);
      setScrolledViewModel(viewModelId);
      openFolders({ folderIds: [folderId], workspaceId: workspace.id });
    },
    [actionLogSender, folderId, openFolders, setScrolledViewModel, workspace.id]
  );

  const [folders, setFolders] = useState<Record<FolderId, Folder> | null>(null);
  const [viewModels, setViewModels] = useState<Record<ViewModelId, ViewModelEntity> | null>(null);

  const [movingItem, setMovingItem] = useState<ViewModelEntity | Folder | null>(null);
  const [destinationFolder, setDestinationFolder] = useState<Folder | null>(null);

  const handleDragEnd = (event: DragEndEvent) => {
    const movingItemId = event.active.id as string; // id が string | number なので、 string に限定する
    const folderId = (event.over?.id || null) as string | null;

    if (movingItemId && folderId) {
      setMovingItem(viewModels?.[movingItemId] || folders?.[movingItemId] || null);

      const folder = parentFolder?.id === folderId ? parentFolder : folders?.[folderId];
      setDestinationFolder(folder || null);
    }

    setDroppableToParentFolder(false);
  };

  // rootFolderTree はワークスペース全体のフォルダ／ビューモデル構成を1つのRTDBノードとして購読しているため、
  // 現在表示中のフォルダとは無関係な箇所への書き込みでも rootFolderTree の参照が変わる。
  // これを直接 useEffect の依存配列に使うと、無関係な変更のたびに一覧のリスナーが張り直され、
  // Loading状態を経由して一覧全体がアンマウント／リマウントされてしまうため、useStableIds で
  // 実際に子フォルダ／子ビューモデルの構成が変わったときだけ参照が変わるようにする。
  const folderIdsOfCurrentFolder = useStableIds(
    currentFolder && currentFolder !== 'loading' ? rootFolderTree.getFolderIdsOf(currentFolder.id) : []
  );
  const viewModelIdsOfCurrentFolder = useStableIds(
    currentFolder && currentFolder !== 'loading' ? rootFolderTree.getViewModelIdsOf(currentFolder.id) : []
  );

  useEffect(() => {
    if (!currentFolder) return;
    if (currentFolder === 'loading') return;

    const repos = Object.fromEntries(
      folderIdsOfCurrentFolder.map((folderId) => [
        folderId,
        new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(folderId)),
      ])
    );

    // 取得対象フォルダが0件の場合には取得完了として空のレコードに設定する。
    // 取得対象フォルダがある場合にはローディング扱いで null に変更する。
    setFolders(Object.entries(repos).length === 0 ? {} : null);

    Object.entries(repos).forEach(([folderId, repo]) =>
      repo.addListener((folder) =>
        setFolders((prev) => {
          if (!folder) {
            const { [folderId]: _, ...rest } = prev || {};
            return rest;
          }

          return {
            ...(prev || {}),
            [folder.id]: folder,
          };
        })
      )
    );

    return () => Object.values(repos).forEach((repo) => repo.removeListener());
  }, [currentFolder, folderIdsOfCurrentFolder]);

  useEffect(() => {
    if (!currentFolder) return;
    if (currentFolder === 'loading') return;

    const repos = Object.fromEntries(
      viewModelIdsOfCurrentFolder.map((viewModelId) => [
        viewModelId,
        new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(viewModelId)),
      ])
    );

    // 取得対象ビューモデルが0件の場合には取得完了として空のレコードに設定する。
    // 取得対象ビューモデルがある場合にはローディング扱いで null に変更する。
    setViewModels(Object.entries(repos).length === 0 ? {} : null);

    Object.entries(repos).forEach(([viewModelId, repo]) =>
      repo.addListener((viewModel) =>
        setViewModels((prev) => {
          if (!viewModel) {
            const { [viewModelId]: _, ...rest } = prev || {};
            return rest;
          }

          return {
            ...(prev || {}),
            [viewModel.id]: viewModel,
          };
        })
      )
    );

    // 取得対象のビューモデルが0件の場合に viewModels を null から空のレコードに更新する
    if (Object.entries(repos).length === 0) {
      setViewModels({});
    }

    return () => Object.values(repos).forEach((repo) => repo.removeListener());
  }, [currentFolder, viewModelIdsOfCurrentFolder]);

  const sortedFolders = useMemo(() => (folders ? Object.values(folders).sort(Folder.compare) : null), [folders]);
  const sortedViewModels = useMemo(
    () => (viewModels ? Object.values(viewModels).sort(ViewModelEntity.compare) : null),
    [viewModels]
  );

  if (currentFolder === 'loading') {
    return <Loading />;
  }

  if (!currentFolder) {
    return (
      <EmptyMessage
        message={
          <>
            {t('workspace.rootFolder.notFound')}
            <br />
            <Link to={UserPagePathBuilder.workspacePage(workspace.id)} className="text-brand underline">
              {t('workspace.rootFolder.backToWorkspace')}
            </Link>
          </>
        }
      />
    );
  }

  return (
    <>
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
        <>
          <div className="mb-3 flex items-center justify-between border-b border-brand">
            {droppableToParentFolder && parentFolder ? (
              <Droppable
                id={parentFolder.id}
                className="mb-1 grow rounded-lg bg-gray-200"
                hoveredClassName="bg-gray-500"
              >
                <div className="py-1 text-center text-2xl">{t('workspace.rootFolder.dropToParent')}</div>
              </Droppable>
            ) : (
              <>
                <div className="justify-start">
                  {parentFolder && (
                    <div className="flex">
                      <Link
                        to={UserPagePathBuilder.workspaceFolderPage(workspace.id, parentFolder.id)}
                        className="text-2xl font-bold text-brand"
                      >
                        <FontAwesomeIcon icon={faAngleLeft} className="w-8" />
                      </Link>
                      <span className="px-2 text-2xl font-bold">{currentFolder.name}</span>
                      <span className="ml-2" />

                      {showEditMenu && (
                        <FolderMenuButtons
                          onEdit={handleClickEditFolder}
                          onDelete={handleClickDeleteFolder}
                          showDuplicateMenu={false}
                          onDuplicateDirectory={() => void 0}
                          onMoveFolder={handleClickMoveFolder}
                          onMoveToOtherWorkspace={handleClickMoveFolderToOtherWorkspace}
                        />
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end p-1">
                  {showEditMenu && (
                    <MenuButtons
                      onCreateFolder={handleClickCreateFolder}
                      onCreateViewModel={handleClickCreateViewModel}
                      onPaste={handlePaste}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          {sortedFolders === null || sortedViewModels === null ? (
            <Loading />
          ) : sortedFolders.length === 0 && sortedViewModels.length === 0 ? (
            <EmptyMessage message={t('workspace.rootFolder.viewModelNotFound')} />
          ) : (
            <>
              <FolderList
                folders={sortedFolders}
                rootFolderTree={rootFolderTree}
                showEditMenu={showEditMenu}
                workspace={workspace}
              />
              <ViewModelList
                viewModels={sortedViewModels}
                showEditMenu={showEditMenu}
                workspace={workspace}
                showConfigureIndividualPermissions={showConfigureIndividualPermissions}
              />
            </>
          )}
        </>
      </DndContext>

      <ViewModelCreateModal
        isOpen={modalTarget === 'CreateViewModel'}
        onSubmit={handleCreateViewModelToRoot}
        onClose={handleCloseModal}
      />

      <FolderCreateModal
        isOpen={modalTarget === 'CreateFolder'}
        workspaceId={workspace.id}
        folderId={folderId}
        onClose={handleCloseModal}
      />

      {currentFolder && currentUserId && (
        <>
          <MoveFolderModal isOpen={modalTarget === 'MoveFolder'} onClose={handleCloseModal} folder={currentFolder} />

          <MoveToOtherWorkspaceModal
            isOpen={modalTarget === 'MoveFolderToOtherWorkspace'}
            onClose={handleCloseModal}
            folder={currentFolder}
            userId={currentUserId}
            groupId={workspace.groupId}
            workspaceId={workspace.id}
          />

          <FolderEditModal folder={currentFolder} isOpen={modalTarget === 'EditFolder'} onClose={handleCloseModal} />

          <FolderDeleteModal
            folder={currentFolder}
            isOpen={modalTarget === 'DeleteFolder'}
            onClose={handleCloseModal}
            hasChildren={(sortedFolders?.length || 0) + (sortedViewModels?.length || 0) > 0}
          />
        </>
      )}

      {destinationFolder &&
        (movingItem instanceof ViewModelEntity ? (
          <MoveViewModelConfirmModal
            isOpen
            onClose={() => {
              setMovingItem(null);
              setDestinationFolder(null);
            }}
            viewModel={movingItem}
            destinationFolder={destinationFolder}
          />
        ) : movingItem instanceof Folder && movingItem.id !== destinationFolder.id ? (
          <MoveFolderConfirmModal
            isOpen
            onClose={() => {
              setMovingItem(null);
              setDestinationFolder(null);
            }}
            folder={movingItem}
            destinationFolder={destinationFolder}
          />
        ) : null)}
    </>
  );
};
